import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { describeError } from '../core/api.ts';

// ---------------------------------------------------------------------------
export function Button({ variant, size, className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'danger' | 'ghost'; size?: 's' }) {
  const cls = ['btn', variant ? `btn-${variant}` : '', size === 's' ? 'btn-s' : '', className ?? ''].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...rest} />;
}

interface FieldProps {
  label: string;
  help?: ReactNode;
  error?: string | null;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}
export function Field({ label, help, error, children }: FieldProps) {
  const id = useId();
  const helpId = help ? `${id}-help` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [helpId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id, describedBy)}
      {help && <span id={helpId} className="field-help">{help}</span>}
      {error && <span id={errId} className="field-error" role="alert">{error}</span>}
    </div>
  );
}

export function TextInput({ label, help, error, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; help?: ReactNode; error?: string | null }) {
  return (
    <Field label={label} help={help} error={error ?? null}>
      {(id, d) => <input id={id} className="input" aria-describedby={d} aria-invalid={error ? true : undefined} {...rest} />}
    </Field>
  );
}

export function TextArea({ label, help, error, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; help?: ReactNode; error?: string | null }) {
  return (
    <Field label={label} help={help} error={error ?? null}>
      {(id, d) => <textarea id={id} className="textarea" aria-describedby={d} aria-invalid={error ? true : undefined} {...rest} />}
    </Field>
  );
}

export function SelectInput({ label, help, error, options, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; help?: ReactNode; error?: string | null; options: { value: string; label: string }[] }) {
  return (
    <Field label={label} help={help} error={error ?? null}>
      {(id, d) => (
        <select id={id} className="select" aria-describedby={d} aria-invalid={error ? true : undefined} {...rest}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function Checkbox({ label, help, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; help?: ReactNode }) {
  const id = useId();
  return (
    <div className="checkbox">
      <input id={id} type="checkbox" {...rest} />
      <label htmlFor={id}>
        {label}
        {help && <div className="field-help">{help}</div>}
      </label>
    </div>
  );
}

// ---------------------------------------------------------------------------
export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'sim' | 'neutral';
export function Badge({ tone = 'neutral', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return <span className={`badge ${tone !== 'neutral' ? `badge-${tone}` : ''}`} title={title}>{children}</span>;
}

export function SimulatedBadge() {
  return <Badge tone="sim" title="Datos simulados: no representan resultados reales">Simulado</Badge>;
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="muted">{subtitle}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </header>
  );
}

export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className ?? ''}`}>
      {(title || actions) && (
        <div className="card-title">
          <span>{title}</span>
          {actions && <span className="row">{actions}</span>}
        </div>
      )}
      {children}
    </section>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="state-box" role="status">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry, title = 'No se pudo cargar' }: { error: unknown; onRetry?: () => void; title?: string }) {
  return (
    <div className="state-box state-error" role="alert">
      <h3>{title}</h3>
      <p>{describeError(error)}</p>
      {onRetry && <Button onClick={onRetry}>Reintentar</Button>}
    </div>
  );
}

export function Loading({ lines = 3, label = 'Cargando…' }: { lines?: number; label?: string }) {
  return (
    <div className="stack-s" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">{label}</span>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

/** Estados estándar de una lectura: carga, error recuperable, vacío. */
export function QueryState<T>({
  query,
  empty,
  isEmpty,
  children,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown; data: T | undefined; refetch: () => unknown };
  empty?: ReactNode;
  isEmpty?: (d: T) => boolean;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const data = query.data as T;
  if (isEmpty?.(data) && empty) return <>{empty}</>;
  return <>{children(data)}</>;
}

export function Notice({ tone = 'info', children, title }: { tone?: 'info' | 'warn' | 'danger' | 'sim'; children: ReactNode; title?: string }) {
  return (
    <div className={`notice ${tone !== 'info' ? `notice-${tone}` : ''}`} role={tone === 'danger' ? 'alert' : 'note'}>
      {title && <strong>{title} </strong>}
      {children}
    </div>
  );
}

/** Función local no disponible en el navegador: explica qué falta y alternativas. */
export function RequiresDesktop({ what, alternatives }: { what: string; alternatives?: ReactNode }) {
  return (
    <Notice tone="warn" title="Requiere aplicación de escritorio.">
      {what} se ejecuta en tu equipo con el motor local (parte B). En la vista web no se simula.
      {alternatives && <div style={{ marginTop: 6 }}>{alternatives}</div>}
    </Notice>
  );
}

// ---------------------------------------------------------------------------
export function Dialog({ open, title, onClose, children, footer }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    }
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="dialog" aria-labelledby={titleId} onClose={onClose} onCancel={onClose}>
      {open && (
        <>
          <div className="dialog-head">
            <h2 id={titleId} style={{ margin: 0 }}>{title}</h2>
            <Button variant="ghost" size="s" onClick={onClose} aria-label="Cerrar">✕</Button>
          </div>
          <div className="dialog-body">{children}</div>
          {footer && <div className="dialog-foot">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

export function Tabs<T extends string>({ value, onChange, items, label }: { value: T; onChange: (v: T) => void; items: { value: T; label: string }[]; label: string }) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.value}
          role="tab"
          type="button"
          className="tab"
          aria-selected={value === it.value}
          tabIndex={value === it.value ? 0 : -1}
          onClick={() => onChange(it.value)}
          onKeyDown={(e) => {
            const idx = items.findIndex((x) => x.value === value);
            if (e.key === 'ArrowRight') onChange(items[(idx + 1) % items.length]!.value);
            if (e.key === 'ArrowLeft') onChange(items[(idx - 1 + items.length) % items.length]!.value);
          }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function KV({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="kv">
      {items.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function LoadMore({ hasMore, loading, onClick }: { hasMore: boolean; loading: boolean; onClick: () => void }) {
  if (!hasMore) return null;
  return (
    <div className="row" style={{ justifyContent: 'center', marginTop: 12 }}>
      <Button onClick={onClick} disabled={loading}>{loading ? 'Cargando…' : 'Cargar más'}</Button>
    </div>
  );
}

export function MutationError({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="field-error" role="alert">{describeError(error)}</p>;
}

// ---------------------------------------------------------------------------
interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'error' | 'info';
}
const ToastCtx = createContext<(text: string, tone?: Toast['tone']) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: Toast['tone'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.tone}`} role={t.tone === 'error' ? 'alert' : 'status'}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  return useContext(ToastCtx);
}
